import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { 
  Save, 
  Loader2, 
  FileText, 
  Mail, 
  Zap, 
  BarChart3, 
  Layout, 
  HelpCircle,
  CreditCard,
  Star,
  Plus,
  Trash2,
  Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CmsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState(null);

  // Default pricing structure with tooltips
  const defaultPricing = {
    section_title: "Planos",
    section_subtitle: "Escolha o plano ideal para o seu negócio",
    disclaimer: "* Um limite antiabuso de 200.000 mensagens por mês se aplica pelo próprio WhatsApp.",
    plans: [
      {
        name: "Trial",
        description: "Experimente grátis por alguns dias",
        price_monthly: "0",
        highlighted: false,
        cta_url: "/login",
        cta_text: "Começar Grátis",
        features: [
          { text: "1 número WhatsApp", tooltip: "Conecte seu número de WhatsApp ou WhatsApp Business" },
          { text: "100 mensagens", tooltip: "Limite total para teste" },
          { text: "Acesso a todas as funcionalidades", tooltip: "Sem restrição de recursos durante o trial" },
          { text: "Sem cartão de crédito", tooltip: "Você não precisa cadastrar cartão" }
        ]
      },
      {
        name: "Light",
        description: "Ideal para pequenos negócios",
        price_monthly: "97",
        highlighted: false,
        cta_url: "/login",
        cta_text: "Começar Agora",
        features: [
          { text: "1 número WhatsApp", tooltip: "Conecte seu número de WhatsApp ou WhatsApp Business" },
          { text: "10.000 mensagens de texto", tooltip: "Mensagens normais de texto + emojis" },
          { text: "2.500 mensagens de mídia", tooltip: "Mensagens com texto + emojis + imagens" },
          { text: "500 mensagens na fila", tooltip: "Quantidade máxima de mensagens aguardando envio simultaneamente na sua campanha" },
          { text: "6 mensagens por minuto", tooltip: "" }
        ]
      },
      {
        name: "Professional",
        description: "Para empresas em crescimento",
        price_monthly: "197",
        highlighted: true,
        cta_url: "/login",
        cta_text: "Começar Agora",
        features: [
          { text: "1 número WhatsApp", tooltip: "Conecte seu número de WhatsApp ou WhatsApp Business" },
          { text: "30.000 mensagens de texto", tooltip: "Mensagens normais de texto + emojis" },
          { text: "10.000 mensagens de mídia", tooltip: "Mensagens com texto + emojis + imagens" },
          { text: "2.000 mensagens na fila", tooltip: "Quantidade máxima de mensagens aguardando envio simultaneamente na sua campanha" },
          { text: "15 mensagens por minuto", tooltip: "" }
        ]
      },
      {
        name: "Business",
        description: "Para operações de grande escala",
        price_monthly: "397",
        highlighted: false,
        cta_url: "/login",
        cta_text: "Começar Agora",
        features: [
          { text: "1 número WhatsApp", tooltip: "Conecte seu número de WhatsApp ou WhatsApp Business" },
          { text: "Mensagens de texto ilimitadas *", tooltip: "Mensagens normais de texto + emojis" },
          { text: "Mensagens de mídia ilimitadas *", tooltip: "Mensagens com texto + emojis + imagens" },
          { text: "10.000 mensagens na fila", tooltip: "Quantidade máxima de mensagens aguardando envio simultaneamente na sua campanha" },
          { text: "100 mensagens por minuto", tooltip: "" }
        ]
      }
    ]
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch CMS content + legal defaults in parallel
      const [cmsRes, legalRes] = await Promise.all([
        fetch(`${API_URL}/api/cms`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/public/legal`),
      ]);
      const data = await cmsRes.json();
      const legalDefaults = legalRes.ok ? await legalRes.json() : {};
      // Ensure pricing structure exists
      // Processar pricing para garantir formato correto
      let processedPricing = data.pricing || defaultPricing;
      
      // Se pricing existe, converter features do formato antigo para o novo
      if (data.pricing && data.pricing.plans) {
        processedPricing = {
          ...data.pricing,
          plans: data.pricing.plans.map((plan, planIndex) => ({
            ...plan,
            features: plan.features.map((feature, featureIndex) => {
              // Se já é objeto, manter
              if (typeof feature === 'object' && feature.text !== undefined) {
                return feature;
              }
              // Se é string, converter para objeto usando defaults
              const defaultPlan = defaultPricing.plans[planIndex];
              const defaultFeature = defaultPlan?.features[featureIndex];
              return {
                text: feature,
                tooltip: defaultFeature?.tooltip || ''
              };
            })
          }))
        };
      }
      
      // Merge legal: keep saved values; fall back to backend defaults so the editor
      // is always pre-filled with the same HTML that the public pages render.
      const existingLegal = data.legal || {};
      const mergedLegal = {
        privacy_policy_html: existingLegal.privacy_policy_html || legalDefaults.privacy_policy_html || '',
        privacy_version: existingLegal.privacy_version || legalDefaults.privacy_version || '',
        terms_of_service_html: existingLegal.terms_of_service_html || legalDefaults.terms_of_service_html || '',
        terms_version: existingLegal.terms_version || legalDefaults.terms_version || '',
      };
      
      setContent({
        ...data,
        pricing: processedPricing,
        legal: mergedLegal,
      });
    } catch (error) {
      toast.error('Erro ao carregar conteúdo');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/cms`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(content)
      });

      if (!response.ok) {
        throw new Error('Erro ao salvar');
      }

      toast.success('Conteúdo salvo com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar conteúdo');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      const keys = path.split('.');
      let current = newContent;
      
      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (key.includes('[')) {
          const [arrayKey, indexStr] = key.split('[');
          const index = parseInt(indexStr.replace(']', ''));
          current = current[arrayKey][index];
        } else {
          current = current[key];
        }
      }
      
      const lastKey = keys[keys.length - 1];
      current[lastKey] = value;
      
      return newContent;
    });
  };

  const updateArrayItem = (arrayName, index, field, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      newContent[arrayName] = [...prev[arrayName]];
      newContent[arrayName][index] = { ...prev[arrayName][index], [field]: value };
      return newContent;
    });
  };

  const updatePricingPlan = (planIndex, field, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      newContent.pricing = { ...prev.pricing };
      newContent.pricing.plans = [...prev.pricing.plans];
      newContent.pricing.plans[planIndex] = { 
        ...prev.pricing.plans[planIndex], 
        [field]: value 
      };
      return newContent;
    });
  };

  const updatePlanFeature = (planIndex, featureIndex, field, value) => {
    setContent(prev => {
      const newContent = { ...prev };
      newContent.pricing = { ...prev.pricing };
      newContent.pricing.plans = [...prev.pricing.plans];
      newContent.pricing.plans[planIndex] = { 
        ...prev.pricing.plans[planIndex],
        features: [...prev.pricing.plans[planIndex].features]
      };
      // Handle both old format (string) and new format (object)
      const currentFeature = prev.pricing.plans[planIndex].features[featureIndex];
      if (typeof currentFeature === 'string') {
        newContent.pricing.plans[planIndex].features[featureIndex] = {
          text: field === 'text' ? value : currentFeature,
          tooltip: field === 'tooltip' ? value : ''
        };
      } else {
        newContent.pricing.plans[planIndex].features[featureIndex] = {
          ...currentFeature,
          [field]: value
        };
      }
      return newContent;
    });
  };

  const addPlanFeature = (planIndex) => {
    setContent(prev => {
      const newContent = { ...prev };
      newContent.pricing = { ...prev.pricing };
      newContent.pricing.plans = [...prev.pricing.plans];
      newContent.pricing.plans[planIndex] = { 
        ...prev.pricing.plans[planIndex],
        features: [...prev.pricing.plans[planIndex].features, { text: "Nova característica", tooltip: "" }]
      };
      return newContent;
    });
  };

  const removePlanFeature = (planIndex, featureIndex) => {
    setContent(prev => {
      const newContent = { ...prev };
      newContent.pricing = { ...prev.pricing };
      newContent.pricing.plans = [...prev.pricing.plans];
      newContent.pricing.plans[planIndex] = { 
        ...prev.pricing.plans[planIndex],
        features: prev.pricing.plans[planIndex].features.filter((_, i) => i !== featureIndex)
      };
      return newContent;
    });
  };

  const addPlan = () => {
    setContent(prev => {
      const newContent = { ...prev };
      newContent.pricing = { ...prev.pricing };
      newContent.pricing.plans = [...prev.pricing.plans, {
        name: "Novo Plano",
        price_monthly: "0",
        highlighted: false,
        cta_text: "Começar Agora",
        stripe_price_id: "",
        features: [{ text: "Característica 1", tooltip: "" }]
      }];
      return newContent;
    });
  };

  const removePlan = (planIndex) => {
    setContent(prev => {
      const newContent = { ...prev };
      newContent.pricing = { ...prev.pricing };
      newContent.pricing.plans = prev.pricing.plans.filter((_, i) => i !== planIndex);
      return newContent;
    });
  };

  // Helper to get feature text (handles both string and object formats)
  const getFeatureText = (feature) => {
    return typeof feature === 'string' ? feature : feature.text;
  };

  // Helper to get feature tooltip (handles both string and object formats)
  const getFeatureTooltip = (feature) => {
    return typeof feature === 'string' ? '' : (feature.tooltip || '');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Tabs defaultValue="hero" className="flex flex-col h-full">
        {/* Fixed Header */}
        <div className="sticky top-0 z-20 bg-background pb-4 border-b">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold">CMS - Gerenciador de Conteúdo</h1>
              <p className="text-muted-foreground">Gerencie os textos e conteúdos da Landing Page</p>
            </div>
            <Button onClick={handleSave} disabled={saving} data-testid="cms-save-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Alterações
                </>
              )}
            </Button>
          </div>

          {/* Fixed Tabs Menu */}
          <TabsList className="grid grid-cols-8 w-full bg-gray-100 dark:bg-gray-800 p-1 rounded-lg">
            <TabsTrigger value="hero" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-teal-400 text-gray-600 dark:text-gray-400 font-medium">
              <Zap className="w-4 h-4" />
              <span className="hidden sm:inline">Hero</span>
            </TabsTrigger>
            <TabsTrigger value="stats" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-teal-400 text-gray-600 dark:text-gray-400 font-medium">
              <BarChart3 className="w-4 h-4" />
              <span className="hidden sm:inline">Stats</span>
            </TabsTrigger>
            <TabsTrigger value="features" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-teal-400 text-gray-600 dark:text-gray-400 font-medium">
              <Layout className="w-4 h-4" />
              <span className="hidden sm:inline">Features</span>
            </TabsTrigger>
            <TabsTrigger value="pricing" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-teal-400 text-gray-600 dark:text-gray-400 font-medium">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Planos</span>
            </TabsTrigger>
            <TabsTrigger value="steps" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-teal-400 text-gray-600 dark:text-gray-400 font-medium">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Passos</span>
            </TabsTrigger>
            <TabsTrigger value="faqs" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-teal-400 text-gray-600 dark:text-gray-400 font-medium">
              <HelpCircle className="w-4 h-4" />
              <span className="hidden sm:inline">FAQ</span>
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-teal-400 text-gray-600 dark:text-gray-400 font-medium">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Contato</span>
            </TabsTrigger>
            <TabsTrigger value="legal" className="flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-teal-700 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-teal-400 text-gray-600 dark:text-gray-400 font-medium" data-testid="cms-legal-tab">
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Legal</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          {/* Hero Tab */}
          <TabsContent value="hero">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Seção Principal (Hero)
              </CardTitle>
              <CardDescription>Primeira seção que aparece no topo do site</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Badge (texto pequeno acima do título)</Label>
                <Input
                  value={content?.hero?.badge || ''}
                  onChange={(e) => {
                    setContent(prev => ({
                      ...prev,
                      hero: { ...prev.hero, badge: e.target.value }
                    }));
                  }}
                  data-testid="hero-badge-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Título - Linha 1</Label>
                <Input
                  value={content?.hero?.title_line1 || ''}
                  onChange={(e) => {
                    setContent(prev => ({
                      ...prev,
                      hero: { ...prev.hero, title_line1: e.target.value }
                    }));
                  }}
                  data-testid="hero-title1-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Título - Linha 2 (destacada em verde)</Label>
                <Input
                  value={content?.hero?.title_line2 || ''}
                  onChange={(e) => {
                    setContent(prev => ({
                      ...prev,
                      hero: { ...prev.hero, title_line2: e.target.value }
                    }));
                  }}
                  data-testid="hero-title2-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Subtítulo</Label>
                <Textarea
                  value={content?.hero?.subtitle || ''}
                  onChange={(e) => {
                    setContent(prev => ({
                      ...prev,
                      hero: { ...prev.hero, subtitle: e.target.value }
                    }));
                  }}
                  data-testid="hero-subtitle-input"
                />
              </div>
              
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-4">Botão "Gerenciar meu plano" no Cabeçalho</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Texto do Botão</Label>
                    <Input
                      value={content?.header?.manage_plan_text || 'Gerenciar meu plano'}
                      onChange={(e) => {
                        setContent(prev => ({
                          ...prev,
                          header: { ...prev.header, manage_plan_text: e.target.value }
                        }));
                      }}
                      placeholder="Gerenciar meu plano"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>URL do Botão</Label>
                    <Input
                      value={content?.header?.manage_plan_url || '/login'}
                      onChange={(e) => {
                        setContent(prev => ({
                          ...prev,
                          header: { ...prev.header, manage_plan_url: e.target.value }
                        }));
                      }}
                      placeholder="/login ou https://..."
                    />
                    <p className="text-xs text-muted-foreground">Use /login para página interna ou URL completa para link externo</p>
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-4">Seção CTA Final</h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Título do CTA</Label>
                    <Input
                      value={content?.cta?.title || ''}
                      onChange={(e) => {
                        setContent(prev => ({
                          ...prev,
                          cta: { ...prev.cta, title: e.target.value }
                        }));
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Subtítulo do CTA</Label>
                    <Input
                      value={content?.cta?.subtitle || ''}
                      onChange={(e) => {
                        setContent(prev => ({
                          ...prev,
                          cta: { ...prev.cta, subtitle: e.target.value }
                        }));
                      }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Estatísticas
              </CardTitle>
              <CardDescription>Números em destaque na barra verde</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {content?.stats?.map((stat, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground">Estatística {index + 1}</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Valor</Label>
                      <Input
                        value={stat.value}
                        onChange={(e) => updateArrayItem('stats', index, 'value', e.target.value)}
                        placeholder="10k+"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Label</Label>
                      <Input
                        value={stat.label}
                        onChange={(e) => updateArrayItem('stats', index, 'label', e.target.value)}
                        placeholder="Mensagens Enviadas"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descrição</Label>
                      <Input
                        value={stat.description}
                        onChange={(e) => updateArrayItem('stats', index, 'description', e.target.value)}
                        placeholder="Capacidade de envio"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features Tab */}
        <TabsContent value="features">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layout className="w-5 h-5" />
                Funcionalidades
              </CardTitle>
              <CardDescription>Cards de funcionalidades do sistema</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {content?.features?.map((feature, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground">Funcionalidade {index + 1}</h4>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Título</Label>
                      <Input
                        value={feature.title}
                        onChange={(e) => updateArrayItem('features', index, 'title', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descrição</Label>
                      <Textarea
                        value={feature.description}
                        onChange={(e) => updateArrayItem('features', index, 'description', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing Tab */}
        <TabsContent value="pricing">
          <div className="space-y-6">
            {/* Pricing Section Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Configurações da Seção de Planos
                </CardTitle>
                <CardDescription>Títulos e textos gerais da seção de preços</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Título da Seção</Label>
                    <Input
                      value={content?.pricing?.section_title || ''}
                      onChange={(e) => {
                        setContent(prev => ({
                          ...prev,
                          pricing: { ...prev.pricing, section_title: e.target.value }
                        }));
                      }}
                      placeholder="Planos"
                      data-testid="pricing-section-title"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Subtítulo da Seção</Label>
                    <Input
                      value={content?.pricing?.section_subtitle || ''}
                      onChange={(e) => {
                        setContent(prev => ({
                          ...prev,
                          pricing: { ...prev.pricing, section_subtitle: e.target.value }
                        }));
                      }}
                      placeholder="Escolha o plano ideal"
                      data-testid="pricing-section-subtitle"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Disclaimer (nota de rodapé)</Label>
                  <Input
                    value={content?.pricing?.disclaimer || ''}
                    onChange={(e) => {
                      setContent(prev => ({
                        ...prev,
                        pricing: { ...prev.pricing, disclaimer: e.target.value }
                      }));
                    }}
                    placeholder="* Condições aplicáveis"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Individual Plans */}
            {content?.pricing?.plans?.map((plan, planIndex) => (
              <Card key={planIndex} className={plan.highlighted ? 'border-primary border-2' : ''}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      {plan.highlighted && <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />}
                      Plano {planIndex + 1}: {plan.name}
                    </CardTitle>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`highlight-${planIndex}`} className="text-sm">Destacar</Label>
                        <Switch
                          id={`highlight-${planIndex}`}
                          checked={plan.highlighted || false}
                          onCheckedChange={(checked) => updatePricingPlan(planIndex, 'highlighted', checked)}
                        />
                      </div>
                      {plan.name?.toLowerCase() !== 'trial' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => removePlan(planIndex)}
                          data-testid={`plan-${planIndex}-remove`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <CardDescription>
                    {plan.highlighted ? 'Este plano aparece destacado como "MAIS POPULAR"' : 'Configure as informações deste plano'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nome do Plano</Label>
                      <Input
                        value={plan.name}
                        onChange={(e) => updatePricingPlan(planIndex, 'name', e.target.value)}
                        placeholder="Light"
                        data-testid={`plan-${planIndex}-name`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descrição</Label>
                      <Input
                        value={plan.description}
                        onChange={(e) => updatePricingPlan(planIndex, 'description', e.target.value)}
                        placeholder="Ideal para pequenos negócios"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Preço Mensal (R$)</Label>
                    <Input
                      value={plan.price_monthly}
                      onChange={(e) => updatePricingPlan(planIndex, 'price_monthly', e.target.value)}
                      placeholder="97"
                      data-testid={`plan-${planIndex}-price-monthly`}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Texto do Botão</Label>
                      <Input
                        value={plan.cta_text || 'Começar Agora'}
                        onChange={(e) => updatePricingPlan(planIndex, 'cta_text', e.target.value)}
                        placeholder="Começar Agora"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Stripe Price ID</Label>
                      <Input
                        value={plan.stripe_price_id || ''}
                        onChange={(e) => updatePricingPlan(planIndex, 'stripe_price_id', e.target.value)}
                        placeholder="price_xxxxxxxxxxxx..."
                        data-testid={`plan-${planIndex}-stripe-price-id`}
                      />
                      <p className="text-xs text-muted-foreground">Obtido no dashboard Stripe &gt; Products &gt; Price ID</p>
                    </div>
                  </div>

                  {plan.name?.toLowerCase() === 'trial' && (
                    <div className="space-y-2 p-3 rounded-md border border-teal-200 bg-teal-50/40">
                      <Label className="text-teal-900">Duração do Trial (dias)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="90"
                        value={plan.trial_days ?? 3}
                        onChange={(e) => updatePricingPlan(planIndex, 'trial_days', parseInt(e.target.value || '0', 10))}
                        placeholder="3"
                        className="max-w-[140px]"
                        data-testid={`plan-${planIndex}-trial-days`}
                      />
                      <p className="text-xs text-teal-800">
                        Após esse período (ou ao atingir o limite de mensagens), a conta é bloqueada
                        e o usuário é forçado a fazer upgrade.
                      </p>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Características do Plano</Label>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => addPlanFeature(planIndex)}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Adicionar
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {plan.features?.map((feature, featureIndex) => (
                        <div key={featureIndex} className="p-3 border rounded-lg bg-gray-50 dark:bg-gray-800/50 space-y-2">
                          <div className="flex items-center gap-2">
                            <Input
                              value={getFeatureText(feature)}
                              onChange={(e) => updatePlanFeature(planIndex, featureIndex, 'text', e.target.value)}
                              placeholder="Característica do plano"
                              className="flex-1"
                            />
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-destructive hover:text-destructive flex-shrink-0"
                              onClick={() => removePlanFeature(planIndex, featureIndex)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-16 flex-shrink-0">Tooltip:</span>
                            <Input
                              value={getFeatureTooltip(feature)}
                              onChange={(e) => updatePlanFeature(planIndex, featureIndex, 'tooltip', e.target.value)}
                              placeholder="Texto explicativo ao passar o mouse (opcional)"
                              className="flex-1 text-sm"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {/* Add Plan Button */}
            <Button
              variant="outline"
              className="w-full border-dashed"
              onClick={addPlan}
              data-testid="cms-add-plan"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Plano
            </Button>
          </div>
        </TabsContent>

        {/* Steps Tab */}
        <TabsContent value="steps">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Como Funciona
              </CardTitle>
              <CardDescription>Passos de como usar o sistema</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {content?.steps?.map((step, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground">Passo {index + 1}</h4>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Título</Label>
                      <Input
                        value={step.title}
                        onChange={(e) => updateArrayItem('steps', index, 'title', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Descrição</Label>
                      <Textarea
                        value={step.description}
                        onChange={(e) => updateArrayItem('steps', index, 'description', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FAQs Tab */}
        <TabsContent value="faqs">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5" />
                Perguntas Frequentes (FAQ)
              </CardTitle>
              <CardDescription>Perguntas e respostas exibidas no site</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {content?.faqs?.map((faq, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground">Pergunta {index + 1}</h4>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Pergunta</Label>
                      <Input
                        value={faq.question}
                        onChange={(e) => updateArrayItem('faqs', index, 'question', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Resposta</Label>
                      <Textarea
                        rows={3}
                        value={faq.answer}
                        onChange={(e) => updateArrayItem('faqs', index, 'answer', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Tab */}
        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Configurações de Contato
              </CardTitle>
              <CardDescription>Configure o e-mail para receber mensagens do formulário de contato</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>E-mail de Contato *</Label>
                <Input
                  type="email"
                  placeholder="contato@seusite.com.br"
                  value={content?.contact_email || ''}
                  onChange={(e) => updateField('contact_email', e.target.value)}
                  data-testid="contact-email-input"
                />
                <p className="text-sm text-muted-foreground">
                  As mensagens enviadas pelo formulário de contato serão enviadas para este e-mail.
                </p>
              </div>
              
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-4">Seção de Contato no Site</h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Título da Seção</Label>
                    <Input
                      value={content?.contact?.title || ''}
                      onChange={(e) => {
                        setContent(prev => ({
                          ...prev,
                          contact: { ...prev.contact, title: e.target.value }
                        }));
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Subtítulo</Label>
                    <Textarea
                      value={content?.contact?.subtitle || ''}
                      onChange={(e) => {
                        setContent(prev => ({
                          ...prev,
                          contact: { ...prev.contact, subtitle: e.target.value }
                        }));
                      }}
                    />
                  </div>
                  
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-4">WhatsApp</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Número do WhatsApp</Label>
                        <Input
                          value={content?.contact?.whatsapp_number || ''}
                          onChange={(e) => {
                            setContent(prev => ({
                              ...prev,
                              contact: { ...prev.contact, whatsapp_number: e.target.value }
                            }));
                          }}
                          placeholder="5511999999999"
                        />
                        <p className="text-xs text-muted-foreground">Formato: código do país + DDD + número (somente números)</p>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Texto do Botão WhatsApp</Label>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Desabilitado</span>
                            <Switch
                              checked={content?.contact?.whatsapp_enabled !== false}
                              onCheckedChange={(checked) => {
                                setContent(prev => ({
                                  ...prev,
                                  contact: { ...prev.contact, whatsapp_enabled: checked }
                                }));
                              }}
                            />
                            <span className="text-xs text-muted-foreground">Habilitado</span>
                          </div>
                        </div>
                        <Input
                          value={content?.contact?.whatsapp_text || ''}
                          onChange={(e) => {
                            setContent(prev => ({
                              ...prev,
                              contact: { ...prev.contact, whatsapp_text: e.target.value }
                            }));
                          }}
                          placeholder="Falar pelo WhatsApp"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-4">E-mail</h4>
                    <div className="space-y-2">
                      <Label>E-mail exibido no site</Label>
                      <Input
                        value={content?.contact?.email_label || ''}
                        onChange={(e) => {
                          setContent(prev => ({
                            ...prev,
                            contact: { ...prev.contact, email_label: e.target.value }
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="legal">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Política de Privacidade & Termos de Uso
              </CardTitle>
              <CardDescription>
                Edite o conteúdo das páginas legais públicas. Aceita HTML básico (h2, h3, p, ul, li, a, strong).
                Sempre que mudar significativamente, atualize a "versão" para gerar novos logs de consentimento.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Privacy */}
              <div className="space-y-3 border rounded-lg p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <Shield className="w-4 h-4 text-teal-600" />
                    Política de Privacidade
                  </h4>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">Versão:</Label>
                    <Input
                      className="w-36 h-8 text-xs"
                      value={content?.legal?.privacy_version || ''}
                      placeholder="2026-02-10"
                      onChange={(e) => setContent(prev => ({ ...prev, legal: { ...(prev?.legal || {}), privacy_version: e.target.value } }))}
                      data-testid="legal-privacy-version-input"
                    />
                  </div>
                </div>
                <Textarea
                  rows={14}
                  className="font-mono text-xs"
                  placeholder="<h2>Política de Privacidade</h2>..."
                  value={content?.legal?.privacy_policy_html || ''}
                  onChange={(e) => setContent(prev => ({ ...prev, legal: { ...(prev?.legal || {}), privacy_policy_html: e.target.value } }))}
                  data-testid="legal-privacy-textarea"
                />
                <p className="text-xs text-muted-foreground">
                  Visualização pública em: <a href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">/politica-de-privacidade</a>
                </p>
              </div>

              {/* Terms */}
              <div className="space-y-3 border rounded-lg p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h4 className="font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4 text-teal-600" />
                    Termos de Uso
                  </h4>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">Versão:</Label>
                    <Input
                      className="w-36 h-8 text-xs"
                      value={content?.legal?.terms_version || ''}
                      placeholder="2026-02-10"
                      onChange={(e) => setContent(prev => ({ ...prev, legal: { ...(prev?.legal || {}), terms_version: e.target.value } }))}
                      data-testid="legal-terms-version-input"
                    />
                  </div>
                </div>
                <Textarea
                  rows={14}
                  className="font-mono text-xs"
                  placeholder="<h2>Termos de Uso</h2>..."
                  value={content?.legal?.terms_of_service_html || ''}
                  onChange={(e) => setContent(prev => ({ ...prev, legal: { ...(prev?.legal || {}), terms_of_service_html: e.target.value } }))}
                  data-testid="legal-terms-textarea"
                />
                <p className="text-xs text-muted-foreground">
                  Visualização pública em: <a href="/termos-de-uso" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline">/termos-de-uso</a>
                </p>
              </div>

              <div className="text-xs text-muted-foreground bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-3">
                <strong>Dica:</strong> os campos já vêm pré-preenchidos com os textos padrão (LGPD). Edite à vontade: os textos exibidos nas páginas públicas serão os que estiverem aqui. Atualize a "Versão" sempre que mudar o conteúdo, para fins de auditoria e consentimento.
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
